import { MINUTE_MS } from './constants';
import { parseInstant } from './timeZone';

// The API sends `deviceData` as AWSJSON that arrives encoded twice (a JSON string holding a JSON string).
const MAX_DECODE_PASSES = 2;

export const parseDeviceData = (raw) => {
  let value = raw;
  try {
    for (let pass = 0; pass < MAX_DECODE_PASSES && typeof value === 'string'; pass += 1) {
      value = JSON.parse(value);
    }
  } catch (error) {
    return {};
  }
  if (value && typeof value === 'object') return value;
  return {};
};

// The id LightMetrics gives the trip an event belongs to (null when the event has none).
export const readLmTripId = (event) => {
  const data = parseDeviceData(event?.deviceData);
  return data.tripID || data.tripId || null;
};

const readIgnition = (value) => {
  if (value === true || value === 1) return 'on';
  if (value === false || value === 0) return 'off';
  if (typeof value !== 'string') return null;
  const text = value.trim().toLowerCase();
  if (text === 'on' || text === '1' || text === 'true') return 'on';
  if (text === 'off' || text === '0' || text === 'false') return 'off';
  return null;
};

export const classifyTrailEvent = (event) => {
  const ignition = readIgnition(event?.ignitionStatus);
  if (ignition === 'off') return 'stopped';
  if (ignition !== 'on') return 'other';
  if (Number(event.speed) > 0) return 'driving';
  return 'idling';
};

export const normalizeTrailEvents = (items) =>
  (items || [])
    .filter(Boolean)
    .map((item) => ({ ...item, timeMs: parseInstant(item.unitTime), lmTripId: readLmTripId(item) }))
    .filter((item) => Number.isFinite(item.timeMs))
    .sort((a, b) => a.timeMs - b.timeMs);

// Segments run from one event to the next; the last one runs to the window end.
export const buildSegments = (events, window) => {
  const segments = [];
  const push = (kind, startMs, endMs) => {
    const last = segments[segments.length - 1];
    if (endMs <= startMs) return;
    if (last && last.kind === kind) {
      last.endMs = endMs;
      return;
    }
    segments.push({ kind, startMs, endMs });
  };

  const inside = events.filter((e) => e.timeMs >= window.start && e.timeMs < window.end);
  if (inside.length === 0) return [];
  push('other', window.start, inside[0].timeMs);
  inside.forEach((event, index) => {
    const next = inside[index + 1];
    let endMs = window.end;
    if (next) endMs = next.timeMs;
    push(classifyTrailEvent(event), event.timeMs, endMs);
  });
  return segments;
};

export const getCameraMarkers = (events, window) =>
  events
    .filter((e) => e.timeMs >= window.start && e.timeMs <= window.end)
    .filter((e) => {
      const flag = parseDeviceData(e.deviceData).hasMedia;
      return flag === true || flag === 'true';
    })
    .map((e) => ({ id: e.id, timeMs: e.timeMs }));

export const buildTicks = (window) => {
  const ticks = [];
  const first = Math.ceil(window.start / MINUTE_MS) * MINUTE_MS;
  for (let ms = first; ms <= window.end; ms += MINUTE_MS) ticks.push(ms);
  return ticks;
};

// Minute marks closer than this to either end are dropped so they do not overlap the limit labels.
const LIMIT_CLEARANCE_PERCENT = 13;

// Labels under the track: the exact start and end of the window, with the full-minute marks between them.
export const buildAxisLabels = (window) => {
  const labels = [{ key: 'start', ms: window.start, percent: 0, kind: 'start' }];
  if (window.end <= window.start) return labels;
  buildTicks(window).forEach((ms) => {
    const percent = toPercent(ms, window);
    if (percent >= LIMIT_CLEARANCE_PERCENT && percent <= 100 - LIMIT_CLEARANCE_PERCENT) {
      labels.push({ key: String(ms), ms, percent, kind: 'tick' });
    }
  });
  labels.push({ key: 'end', ms: window.end, percent: 100, kind: 'end' });
  return labels;
};

export const toPercent = (ms, window) => {
  const span = window.end - window.start;
  if (span <= 0) return 0;
  return Math.min(100, Math.max(0, ((ms - window.start) / span) * 100));
};

export const fromPercent = (percent, window) =>
  window.start + (Math.min(1, Math.max(0, percent)) * (window.end - window.start));
